import { Info, Sparkles } from 'lucide-react'

import EmptyCard from './EmptyCard'

import type { ImpactResponse } from '@/api/teamInsights'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

interface PromptQualityCardProps {
  response: ImpactResponse['ai_prompt_quality']
}

const PromptQualityCard = ({ response }: PromptQualityCardProps) => {
  const multiplier = response.multiplier

  if (multiplier === null || multiplier === undefined) {
    return (
      <EmptyCard
        icon={<Sparkles className="size-5" />}
        title="AI prompt quality"
        description="Insights will appear once we have enough data to calculate this trend"
      />
    )
  }

  return (
    <Card className="pt-0">
      <CardHeader className="flex items-center gap-4 space-y-0 border-b pl-6 pr-3 !py-3 sm:flex-row">
        <div className="flex flex-1 flex-col gap-0.5">
          <CardTitle className="text-xl font-semibold">AI prompt quality</CardTitle>
          <p className="flex gap-1.5 text-[15px] leading-5 text-muted-foreground">
            Based on team&apos;s dictated prompt lengths
            <Tooltip>
              <TooltipTrigger
                aria-label="About AI prompt quality"
                className="inline-flex items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--fathom)]"
              >
                <Info className="size-4" />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[320px] text-muted-foreground">
                When your team uses Flow to dictate prompts in AI tools like ChatGPT and Claude, they create longer,
                more detailed prompts than they would type. Longer prompts give AI models more context, resulting in
                better outputs. This is calculated from your team&apos;s average dictation length in AI tools vs. the
                typical typed prompt length of 8 words.
              </TooltipContent>
            </Tooltip>
          </p>
        </div>
      </CardHeader>
      <CardContent className="flex min-h-[250px] flex-1 flex-col items-center justify-center gap-2 px-6">
        <div className="text-[56px] font-semibold leading-none tracking-tight text-foreground">
          {multiplier.toFixed(1)}x
        </div>
        <div className="text-[18px] leading-5">Improvement in prompt quality</div>
      </CardContent>
    </Card>
  )
}

export default PromptQualityCard
